import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Subscription } from 'rxjs';
import { ProductDetail } from 'src/@omnial/_models/catalog/product.model';
import { Customer } from 'src/@omnial/_models/customer/customer.model';
import { ReviewRequest, ReviewResult } from 'src/@omnial/_models/customer/review.model';
import { CustomerService } from 'src/@omnial/_services/customer/customer.service';
import { ReviewService } from 'src/@omnial/_services/customer/review.service';
import { EmailRegEx } from 'src/@omnial/_utils/app-validators';
import { ProductZoomComponent } from 'src/@omnial/pages/product/product-zoom/product-zoom.component';

@Component({
    selector: 'app-product-review-submit-dialog',
    templateUrl: './../../../app/shared/product-review-submit-dialog/product-review-submit-dialog.component.html',
    styleUrls: ['./../../../app/shared/product-review-submit-dialog/product-review-submit-dialog.component.scss'],
    standalone: false
})

export class ProductReviewSubmitDialogComponent implements OnDestroy {
  public noStars = 0;
  public reviewed: boolean = false;
  public customer: Customer;
  public subscriptions: Subscription[] = [];

  public reviewForm = new FormGroup({
    rating: new FormControl(null as number, Validators.compose([Validators.required])),
    title: new FormControl('', Validators.compose([Validators.required])),
    reviewText: new FormControl('', Validators.compose([Validators.required])),
    name: new FormControl('', Validators.compose([Validators.required])),
    email: new FormControl('', Validators.compose([Validators.required, Validators.email, EmailRegEx]))
  });

  constructor(
    public dialogRef: MatDialogRef<ProductZoomComponent>,
    @Inject(MAT_DIALOG_DATA) public product: ProductDetail,
    public customerService: CustomerService,
    public recaptchaV3Service: ReCaptchaV3Service,
    public reviewService: ReviewService,
    public snackBar: MatSnackBar) {
    this.subscriptions.push(this.customerService.customer.subscribe((res) => {
      if (res) {
        this.customer = res as Customer;
        this.reviewForm.controls.name.patchValue(this.customer.firstName);
        this.reviewForm.controls.email.patchValue(this.customer.email);
      }
    }));
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  public changeRating(rating: number) {
    this.noStars = rating;
    this.reviewForm.controls.rating.patchValue(rating);
  }

  public changeStars(event: MatRadioChange) {
    this.noStars = event.value as number;
  }

  public onReviewSubmit(): void {
    if (this.reviewForm.valid) {
      this.subscriptions.push(this.recaptchaV3Service.execute('ProductReview').subscribe(
        token => {
          const review = new ReviewRequest();
          review.token = token;
          review.rating = this.reviewForm.controls.rating.value;
          review.title = this.reviewForm.controls.title.value;
          review.reviewText = this.reviewForm.controls.reviewText.value;
          review.name = this.reviewForm.controls.name.value;
          review.email = this.reviewForm.controls.email.value;
          review.productId = this.product.id;
          if (this.customer) {
            review.customerGuid = this.customer.customerGuid;
          }
          this.subscriptions.push(this.reviewService.submit(review).subscribe(
            resetRes => {
              const result = resetRes as ReviewResult;
              if (result.success) {
                if (result.customerGuid && !this.customerService.customer) {  // New Guest Customer
                  const customer = new Customer();
                  customer.customerGuid = result.customerGuid;
                  this.customerService.saveCustomerCookie(customer);
                }
                this.reviewForm.controls.title.patchValue('');
                this.reviewForm.controls.reviewText.patchValue('');
                this.reviewForm.controls.rating.patchValue(null);
                this.reviewed = true;
                const infoMessage = 'Your review has been submitted.';
                this.snackBar.open(infoMessage, 'X', { panelClass: 'success', verticalPosition: 'top', duration: 6000 });
                this.dialogRef.close();
              } else {
                let errMessage = 'Review submission failed';
                if (result.errors?.length > 0) {
                  for (const error of result.errors) {
                    errMessage += ` ... ${error}`;
                  }
                }
                this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
              }
            }));
        }));

    } else {
      const message = 'You seem to have missed something';
      this.snackBar.open(message, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
    }
  }

  public close(): void {
    this.dialogRef.close();
  }
}
